import { isEmpty } from "lodash";
import { DateTime } from "luxon";
import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import FormControl from "@mui/material/FormControl";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import React, { useEffect, useMemo, useState } from "react";
import {
  Link,
  redirect,
  useLoaderData,
  useLocation,
  useNavigate,
} from "react-router-dom";
import styled from "styled-components";
import { APIProvider } from "@vis.gl/react-google-maps";

// Imported actions
// import { getAlerts } from "../../actions/alert";
import { searchAlerts, searchChargingStationsFull } from "../../../actions/search";

// Imported assets
import CaretDownBlack from "../../../assets/icons/DownCarrot_BlackIcon.svg";
import CaretDownWhite from "../../../assets/icons/DownCarrot_WhiteIcon.svg";
import CaretUpBlack from "../../../assets/icons/UpCarrot_BlackIcon.svg";
import CaretUpWhite from "../../../assets/icons/UpCarrot_WhiteIcon.svg";
import { ReactComponent as UpCaretIcon } from "../../../assets/icons/UpCarrot_WhiteIcon.svg";
import { ReactComponent as DownCaretIcon } from "../../../assets/icons/DownCarrot_WhiteIcon.svg";
import CaliforniaPlaceholder from "../../../assets/temp/State_California.png";
import ColoradoPlaceholder from "../../../assets/temp/State_Colorado.png";

// Imported components
import Label from "../../../components/form/Label";
import Map from "./components/Map";
import NavigationBar from "../../../components/nav/NavigationBar";
import PrimaryButton from "../../../components/ui/PrimaryButton";
import SecondaryButton from "../../../components/ui/SecondaryButton";

// Imported contexts
import { useUserContext } from "../../../contexts/User";

// Imported utils
import { isLoggedIn } from "../../../utils/user";

// Styled components
const AlertsSection = styled.div`
  background: white;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 40px;
  height: 100%;
  padding: 8px;
  width: 100%;
`;

const AlertsSectionCanvas = styled.div`
  background: var(--terbine-light-gray);
  border-radius: 8px;
  display: flex;
  flex-direction: row;
  flex-grow: 5;
  height: 100%;
  justify-content: center;
  max-width: 100%;
  overflow: hidden;
  overflow-x: auto;
  padding: 10px;
  position: relative;
  width: 100%;

  @media (max-width: 85rem) {
    margin-bottom: -16px;
    min-width: auto;
    order: 1;
    width: 100%;
  }
`;

const AnalyticsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  height: 100%;
  justify-content: space-around;
  width: 100%;
`;

const AnalyticsSectionCanvas = styled.div`
  background: var(--terbine-light-gray);
  border-radius: 8px;
  flex-grow: 1;
  padding: 20px;

  @media (max-width: 85rem) {
    order: 3;
  }
`;

const AnalyticsSectionControls = styled.div`
  align-self: flex-end;
`;

const AnalyticsSectionGraphContainer = styled.div`
  border-radius: 8px;
  cursor: pointer;
  position: relative;
`;

const AnalyticsSectionHeader = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const AnalyticsSectionSubtitle = styled.div`
  color: black;
  font-family: Inter, Oxygen, Roboto, sans-serif;
  font-size: max(1.1vw, 16px);
`;

const AnalyticsSectionTitle = styled.div`
  color: black;
  font-family: Oxygen, Inter, Roboto, sans-serif;
  font-size: max(1.3vw, 20px);
  font-weight: bolder;
`;

const Container = styled.div`
  align-items: center;
  display: flex;
  flex-direction: row;
  gap: 20px;
  height: 100vh;
  justify-content: flex-start;
  padding: 100px 20px 20px 20px;
  position: relative;

  @media (max-width: 85rem) {
    align-items: flex-start;
    flex-direction: column;
    height: auto;
  }
`;

const DrillDownButton = styled(SecondaryButton)`
  height: 40px;
  width: 25%;

  &:hover {
    background: lab(from white calc(l - 5) a b / 100);
  }
`;

const FormField = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 4px;
  justify-content: flex-start;
  position: relative;

  & * {
    font-family: Inter, Oxygen, Roboto, sans-serif !important;
  }

  & .MuiInputBase-root {
    background: white;
    height: 40px;
  }
`;

const InfoRow = styled.div`
  align-items: flex-end;
  display: flex;
  flex-direction: row;
  gap: 12px;
  justify-content: space-between;
  margin-bottom: 10px;
  position: relative;
  width: 100%;

  .MuiInputBase-input {
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const MapCanvas = styled.div`
  background: var(--terbine-light-gray);
  border-radius: 8px;
  display: flex;
  flex-direction: row;
  flex-grow: 5;
  padding: 10px;
  position: relative;

  & .dashboard-map > div {
    border-radius: 8px;
  }

  @media (max-width: 85rem) {
    order: 2;

    .dashboard-map {
      height: 300px;
      width: 100%;
    }
  }
`;

const PlaceholderImg = styled.img`
  border-radius: 8px;
  height: 100%;
  width: 100%;
`;

const RightSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  height: 100%;
  min-width: 500px;
  width: 50%;

  @media (max-width: 85rem) {
    min-width: auto;
    order: 4;
    width: 100%;
  }
`;

const SectionContent = styled.div`
  align-items: center;
  align-self: stretch;
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: relative;
  width: 100%;
`;

const SectionDescription = styled.div`
  color: black;
  font-family: Inter, Oxygen, Roboto, sans-serif;
  font-size: clamp(12px, 1.5vw, 16px);
  font-weight: 400;
  line-height: clamp(18px, 2.5vw, 24px);
  position: relative;
  text-align: center;
  width: 520px;
`;

const SectionHeader = styled.div`
  align-items: center;
  align-self: stretch;
  display: flex;
  flex-direction: column;
  gap: clamp(4px, 1vw, 8px);
  justify-content: flex-start;
  position: relative;
`;

const SectionHeaderContainer = styled.div`
  align-items: center;
  align-self: stretch;
  display: flex;
  flex-direction: row;
  height: 161px;
  justify-content: center;
  mid-width: 1000px;
  padding: 40px 170px 0px 170px;
  position: relative;

  @media (max-width: 85rem) {
    margin-bottom: clamp(-200px, 1.5vw, -60px);
  }
`;

const SectionTitle = styled.div`
  color: black;
  font-family: Oxygen, Inter, Roboto, sans-serif;
  font-size: clamp(20px, 4vw, 40px);
  font-weight: 700;
  line-height: clamp(28px, 5vw, 48px);
  text-align: center;
  position: relative;
  width: 520px;
`;

const ViewAlertButton = styled(PrimaryButton)`
  background: var(--terbine-blue);
  height: 40px;
  width: 25%;

  &:hover:enabled {
    background: lab(from var(--terbine-blue) calc(l - 5) a b / 100);
  }
`;

const timeIntervalOptions = [
  {
    id: 1,
    codeName: "Year to Date",
  },
  {
    id: 2,
    codeName: "Past Year",
  },
  {
    id: 3,
    codeName: "Past Month",
  },
  {
    id: 4,
    codeName: "Past Week",
  },
  {
    id: 5,
    codeName: "Past 24 Hours",
  },
  {
    id: 6,
    codeName: "Past 12 Hours",
  },
];

const initialPage = 0,
  initialPageSize = 10,
  initialSortBy = "reportedAt",
  initialSortDirection = "DESC";

// Loader for router
export const loader = async () => {
  if (!isLoggedIn()) {
    return redirect("/login");
  } else {
    const initialAlerts = await searchAlerts(
      initialPage,
      99999, // initialPageSize,
      initialSortBy,
      initialSortDirection
    );

    const chargingStations = await searchChargingStationsFull(false, 0, 99999);

    return {
      initialAlerts,
      chargingStations,
    };
  }
};

const Dashboard = () => {
  const loaderData = useLoaderData();
  const appLocation = useLocation();
  const navigate = useNavigate();

  const { user } = useUserContext();

  const [alerts, setAlerts] = useState(loaderData.initialAlerts.data);
  const [paginationData, setPaginationData] = useState(
    loaderData.initialAlerts.headers
  );
  const [chargingStations /*, setChargingStations*/] = useState(
    loaderData.chargingStations
  );

  // State to track which past alert is expanded
  const [expandedAlert, setExpandedAlert] = useState(null);
  const [selectedStation, setSelectedStation] = useState(null);

  // Alerts table settings
  const [tablePage, setTablePage] = useState(initialPage);
  const [tableRowsPerPage, setTableRowsPerPage] = useState(initialPageSize);
  const [tableSortType, setTableSortType] = useState("Timestamp");
  const [tableSortDirection, setTableSortDirection] = useState("DESC");

  // Analytics section graph settings
  const [timeInterval, setTimeInterval] = useState(3);

  useEffect(() => {
    const getNewAlerts = async () => {
      let sortType;

      switch (tableSortType) {
        case "Timestamp":
        default: {
          sortType = "reportedAt";
          break;
        }
      }

      await searchAlerts(0, 99999, sortType, tableSortDirection)
        .then(async (resp) => {
          setAlerts(resp.data);
          setPaginationData(resp.headers);
        })
        .catch((err) => {
          // handle error
        });
    };

    if (!isLoggedIn()) {
      navigate("/login", {
        replace: true,
        state: { path: appLocation.pathname },
      });
    } else {
      getNewAlerts();
    }
  }, [/*tablePage, tableRowsPerPage,*/ tableSortType, tableSortDirection]);

  // Handler to toggle past alert dropdown
  const handleAlertToggle = (alertId, stationId) => {
    setExpandedAlert((prev) => (prev === alertId ? null : alertId));
    setSelectedStation((prev) => (prev === stationId ? null : stationId));
  };

  const handleHeaderClick = (e, sortBy) => {
    e?.preventDefault();

    setExpandedAlert(null);

    if (tableSortType !== sortBy) {
      setTableSortType(sortBy);
      setTableSortDirection("DESC");
    } else if (tableSortDirection === "DESC") {
      setTableSortDirection("ASC");
    } else setTableSortDirection("DESC");
  };

  // Returns only alerts for current page
  const visibleAlerts = useMemo(
    () =>
      [...alerts].slice(
        tablePage * tableRowsPerPage,
        tablePage * tableRowsPerPage + tableRowsPerPage
      ),
    [alerts, tablePage, tableRowsPerPage, tableSortDirection, tableSortType]
  );

  const visibleStations = useMemo(() => {
    let currStations = visibleAlerts.map((a) => a.stationId);
    return chargingStations.filter((s) => {
      return currStations.includes(s.id);
    });
  }, [chargingStations, visibleAlerts]);

  const AlertRow = ({ data, i }) => {
    // Table page and rows per page for the alert's notification table
    const [tablePage, setTablePage] = useState(0);
    const [tableRowsPerPage, setTableRowsPerPage] = useState(10);

    const fullAddress = `${data.addressLine1}${
      data.addressLine2 ? `, ${data.addressLine2}` : ""
    }${data.city ? `, ${data.city}` : ""}${
      data.state ? `, ${data.state}` : ""
    }${data.postalCd ? `, ${data.postalCd}` : ""}${
      data.countryCd ? `, ${data.countryCd}` : ""
    }`;

    return (
      <>
        <TableRow
          onClick={() => handleAlertToggle(data.id, data.stationId)}
          style={{
            WebkitUserSelect: "none" /* Safari */,
            MozUserSelect: "none" /* Firefox */,
            msUserSelect: "none" /* IE10+/Edge */,
            userSelect: "none" /* Standard */,
          }}
          sx={{
            background: "#F2F2F2",
            cursor: "pointer",
            "& td": {
              borderBottom: "0",
              borderTop: "3px solid white",
              color: "black",
            },
            "&:first-child td": { borderTop: "0" },
          }}
        >
          <Tooltip
            arrow
            followCursor={true}
            placement="top"
            title={fullAddress}
          >
            <TableCell
              align="left"
              sx={{
                color: "white",
                maxWidth: 175,
                minWidth: 125,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              <span>{fullAddress}</span>
            </TableCell>
          </Tooltip>
          <Tooltip
            arrow
            followCursor={true}
            placement="top"
            title={data.stationId}
          >
            <TableCell
              align="left"
              sx={{
                color: "white",
                maxWidth: 125,
                minWidth: 75,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {data.stationId}
            </TableCell>
          </Tooltip>
          <Tooltip
            arrow
            followCursor={true}
            placement="top"
            title={data.equipmentType.codeName}
          >
            <TableCell
              align="left"
              sx={{
                color: "white",
                maxWidth: 100,
                minWidth: 50,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {data.equipmentType.codeName}
            </TableCell>
          </Tooltip>
          <Tooltip
            arrow
            followCursor={true}
            placement="top"
            title={data.type.codeName}
          >
            <TableCell
              align="left"
              sx={{
                color: "white",
                maxWidth: 125,
                minWidth: 75,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {data.type.codeName}
            </TableCell>
          </Tooltip>
          <Tooltip
            arrow
            followCursor={true}
            placement="top"
            title={
              DateTime.fromISO(data.reportedAt).toLocaleString(
                DateTime.DATE_SHORT
              ) +
              " " +
              DateTime.fromISO(data.reportedAt).toLocaleString(
                DateTime.TIME_WITH_SECONDS
              )
            }
          >
            <TableCell
              align="left"
              sx={{
                color: "white",
                maxWidth: 150,
                minWidth: 100,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {DateTime.fromISO(data.reportedAt).toLocaleString(
                DateTime.DATE_SHORT
              )}{" "}
              {DateTime.fromISO(data.reportedAt).toLocaleString(
                DateTime.TIME_WITH_SECONDS
              )}
            </TableCell>
          </Tooltip>
          <TableCell
            align="left"
            sx={{
              color: "white",
              maxWidth: 100,
              minWidth: 50,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {data.status.codeName}
          </TableCell>
          <TableCell sx={{ width: 25 }}>
            <IconButton
              size="small"
              sx={{ color: "white", cursor: "pointer", "& > svg": { mr: 0 } }}
            >
              {expandedAlert === data.id ? (
                <UpCaretIcon fill="black" width="12px" />
              ) : (
                <DownCaretIcon fill="black" width="12px" />
              )}
            </IconButton>
          </TableCell>
        </TableRow>
        <TableRow
          className="dashboard-alerts-expanded-row"
          sx={{
            mb: expandedAlert === data.id ? "10px" : "0",

            "& td:first-child": { borderBottomLeftRadius: "8px" },
            "& td:last-child": { borderBottomRightRadius: "8px" },
          }}
        >
          <TableCell
            colSpan={8}
            style={{
              borderBottom: 0,
              padding: "0",
            }}
          >
            <Collapse
              in={expandedAlert === data.id}
              timeout="auto"
              unmountOnExit
            >
              <Box
                sx={{
                  background: "#FAFAFA",
                  borderTop: "none",
                  padding: "20px",
                }}
              >
                <InfoRow>
                  <FormField style={{ width: "45%" }}>
                    <Label>Location</Label>
                    <FormControl sx={{ width: "100%", minWidth: "200px" }}>
                      <Tooltip arrow placement="top" title={fullAddress}>
                        <TextField disabled value={fullAddress} />
                      </Tooltip>
                    </FormControl>
                  </FormField>

                  <FormField style={{ width: "25%" }}>
                    <Label>Fault Type</Label>
                    <FormControl sx={{ width: "100%", minWidth: "100px" }}>
                      <Tooltip arrow placement="top" title={data.type.codeName}>
                        <TextField disabled value={data.type.codeName} />
                      </Tooltip>
                    </FormControl>
                  </FormField>

                  <FormField style={{ width: "25%" }}>
                    <Label>Timestamp</Label>
                    <FormControl sx={{ width: "100%", minWidth: "100px" }}>
                      <Tooltip
                        arrow
                        placement="top"
                        title={
                          DateTime.fromISO(data.reportedAt).toLocaleString(
                            DateTime.DATE_SHORT
                          ) +
                          " " +
                          DateTime.fromISO(data.reportedAt).toLocaleString(
                            DateTime.TIME_WITH_SECONDS
                          )
                        }
                      >
                        <TextField
                          disabled
                          value={`${
                            "" +
                            DateTime.fromISO(data.reportedAt).toLocaleString(
                              DateTime.DATE_SHORT
                            ) +
                            " " +
                            DateTime.fromISO(data.reportedAt).toLocaleString(
                              DateTime.TIME_WITH_SECONDS
                            )
                          }`}
                        />
                      </Tooltip>
                    </FormControl>
                  </FormField>
                </InfoRow>

                <InfoRow>
                  <FormField style={{ width: "45%" }}>
                    <Label>Equipment</Label>
                    <FormControl sx={{ width: "100%", minWidth: "200px" }}>
                      <Tooltip
                        arrow
                        placement="top"
                        title={`${data.equipmentType.codeName} / Equipment ID: ${data.equipmentId}`}
                      >
                        <TextField
                          disabled
                          value={`${data.equipmentType.codeName} / Equipment ID: ${data.equipmentId}`}
                        />
                      </Tooltip>
                    </FormControl>
                  </FormField>

                  <DrillDownButton style={{ padding: 0 }} type="button">
                    <Link
                      state={{
                        station: data.stationId,
                        equipment: data.equipmentId,
                      }}
                      style={{
                        height: "100%",
                        lineHeight: "40px",
                        textAlign: "center",
                        width: "100%",
                      }}
                      to={"/console-view"}
                    >
                      Drill Down
                    </Link>
                  </DrillDownButton>

                  <ViewAlertButton style={{ padding: 0 }} type="button">
                    <Link
                      state={{ alert: data.id }}
                      style={{
                        height: "100%",
                        lineHeight: "40px",
                        textAlign: "center",
                        width: "100%",
                      }}
                      to={"/view-alerts"}
                    >
                      View Full Alert
                    </Link>
                  </ViewAlertButton>
                </InfoRow>

                {!isEmpty(data.notifications) && (
                  <>
                    <TableContainer
                      className={"dashboard-alerts-notifications-table"}
                      id={`notifications-table-${i}`}
                      sx={{
                        border: "1px solid #D9D9D9",
                        borderRadius: "8px",
                        mt: "20px",
                      }}
                    >
                      <Table size="small">
                        <TableHead>
                          <TableRow
                            sx={{
                              background: "var(--terbine-blue)",
                              "& th": { color: "white", fontWeight: "400" },
                            }}
                          >
                            <TableCell>
                              Notification Name
                            </TableCell>
                            <TableCell>
                              Recipient Name
                            </TableCell>
                            <TableCell>
                              Type
                            </TableCell>
                            <TableCell>
                              Status
                            </TableCell>
                            <TableCell>
                              Time Sent
                            </TableCell>
                            <TableCell>
                              Contact Info
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {data.notifications.map((notif, j) => (
                            <TableRow
                              key={`notifications-table-${i}-row-${j}`}
                              sx={{
                                background: "white",

                                "& td": { borderBottom: "1px solid #D9D9D9" },

                                "&:last-child td": { borderBottom: "none" },
                              }}
                            >
                              <TableCell>
                                {notif.notificationName || "Unknown"}
                              </TableCell>
                              <TableCell>
                                {notif.contact?.contactName || "Unknown"}
                              </TableCell>
                              <TableCell>
                                {notif.notificationType.codeName}
                              </TableCell>
                              <TableCell>{notif.status.codeName}</TableCell>
                              <TableCell>{`${
                                "" +
                                DateTime.fromISO(notif.sentAt).toLocaleString(
                                  DateTime.DATE_SHORT
                                ) +
                                " " +
                                DateTime.fromISO(notif.sentAt).toLocaleString(
                                  DateTime.TIME_WITH_SECONDS
                                )
                              }`}</TableCell>
                              <TableCell>{notif.notificationValue}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    <TablePagination
                      component="div"
                      count={data.notifications.length}
                      onPageChange={(e, newPage) => {
                        setTablePage(newPage);
                        document
                          .getElementById(`notifications-table-${i}`)
                          ?.scrollIntoView({ behavior: "smooth" });
                      }}
                      onRowsPerPageChange={(e) => {
                        setTableRowsPerPage(parseInt(e.target.value, 10));
                        setTablePage(0);
                        document
                          .getElementById(`notifications-table-${i}`)
                          ?.scrollIntoView({ behavior: "smooth" });
                      }}
                      page={tablePage}
                      rowsPerPage={tableRowsPerPage}
                      rowsPerPageOptions={[5, 10, 25]}
                    />
                  </>
                )}
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      </>
    );
  };

  return (
    <Container>
      <NavigationBar />

      <AlertsSectionCanvas>
        <AlertsSection id="alerts-section">
          <SectionHeaderContainer>
            <SectionHeader>
              <SectionTitle>View Alerts</SectionTitle>
              <SectionDescription>
                View information on Past Alerts and Manage Trouble Tickets
              </SectionDescription>
            </SectionHeader>
          </SectionHeaderContainer>

          <TableContainer
            id="dashboard-alerts-table-container"
            sx={{
              width: "100%",
              maxWidth: "100%",
              overflowX: "auto",
              display: "block",
              padding: "20px",
            }}
          >
            <div
              style={{
                borderRadius: "8px",
                outline: "3px solid white",
                width: "100%",
                maxWidth: "100%",
                overflowX: "auto",
                display: "block",
              }}
            >
              <Table>
                <TableHead id="dashboard-alerts-table-head">
                  <TableRow
                    sx={{
                      backgroundColor: "var(--terbine-blue)",

                      "& th": {
                        color: "var(--terbine-white)",
                        fontSize: "16px",
                        fontWeight: "400",
                      },
                    }}
                  >
                    <TableCell
                      align="left"
                      // onClick={(e) => handleHeaderClick(e, "Location")}
                      sx={{
                        /*cursor: "pointer",*/ maxWidth: 175,
                        minWidth: 125,
                      }}
                    >
                      Location
                    </TableCell>
                    <TableCell
                      align="left"
                      sx={{ maxWidth: 125, minWidth: 75 }}
                    >
                      Station ID
                    </TableCell>
                    <TableCell
                      align="left"
                      sx={{ maxWidth: 100, minWidth: 50 }}
                    >
                      Section
                    </TableCell>
                    <TableCell
                      align="left"
                      // onClick={(e) => handleHeaderClick(e, "Error Status")}
                      sx={{
                        /*cursor: "pointer",*/ maxWidth: 125,
                        minWidth: 75,
                      }}
                    >
                      Fault Type
                    </TableCell>
                    <TableCell
                      align="left"
                      onClick={(e) => handleHeaderClick(e, "Timestamp")}
                      sx={{ cursor: "pointer", maxWidth: 150, minWidth: 100 }}
                    >
                      Timestamp{" "}
                      {tableSortType === "Timestamp" && (
                        tableSortDirection === "DESC" ? (
                          <DownCaretIcon fill="white" width="12px" />
                        ) : (
                          <UpCaretIcon fill="white" width="12px" />
                        )
                      )}
                    </TableCell>
                    <TableCell
                      align="left"
                      sx={{ maxWidth: 100, minWidth: 50 }}
                    >
                      Status
                    </TableCell>
                    <TableCell sx={{ width: 25 }} />
                  </TableRow>
                </TableHead>
                <TableBody
                  sx={{
                    "& > .MuiTableRow-root:last-child > .MuiTableCell-root": {
                      padding: "0 !important",
                    },
                  }}
                >
                  {isEmpty(visibleAlerts) && (
                    <TableRow>
                      <TableCell>No alerts</TableCell>
                      <TableCell colSpan={7} />
                    </TableRow>
                  )}

                  {!isEmpty(visibleAlerts) &&
                    visibleAlerts.map((alert, i) => (
                      <AlertRow data={alert} i={i} key={`alert-row-${i}`} />
                    ))}
                </TableBody>
              </Table>
            </div>
            <TablePagination
              component="div"
              count={parseInt(paginationData["x-total-count"])}
              onPageChange={(e, newPage) => {
                setExpandedAlert(null);
                setSelectedStation(null);
                setTablePage(newPage);
                document
                  .getElementById("dashboard-alerts-table-container")
                  ?.scrollIntoView({ behavior: "smooth" });
              }}
              onRowsPerPageChange={(e) => {
                setExpandedAlert(null);
                setSelectedStation(null);
                setTableRowsPerPage(parseInt(e.target.value, 10));
                setTablePage(0);
                document
                  .getElementById("dashboard-alerts-table-container")
                  ?.scrollIntoView({ behavior: "smooth" });
              }}
              page={tablePage}
              rowsPerPage={tableRowsPerPage}
              rowsPerPageOptions={[5, 10, 25]}
            />
          </TableContainer>
        </AlertsSection>
      </AlertsSectionCanvas>

      <RightSection>
        <MapCanvas>
          <APIProvider apiKey={"AIzaSyBy0cowEjTB0suohBM4h4UdiSz-ng-YPWg"}>
            <Map markers={visibleStations} selectedStation={selectedStation} />
          </APIProvider>
        </MapCanvas>

        <AnalyticsSectionCanvas>
          <AnalyticsSection>
            <AnalyticsSectionHeader>
              {/* TODO: Remove temp userId check below */}
              <AnalyticsSectionTitle>
                {user.userId === "tony.k.walters@cnmp.com" ? "Region Uptime - California" : "Region Uptime - Colorado"}
              </AnalyticsSectionTitle>
              <AnalyticsSectionSubtitle>97% Uptime</AnalyticsSectionSubtitle>
              <AnalyticsSectionControls>
                <FormField style={{ flexGrow: 1 }}>
                  <FormControl sx={{ width: "200px" }}>
                    <Select
                      id="time-interval-select"
                      onChange={(e) => setTimeInterval(Number(e.target.value))}
                      // size="small"
                      value={timeInterval}
                    >
                      {timeIntervalOptions &&
                        timeIntervalOptions.map((type, i) => (
                          <MenuItem
                            key={`time-interval-menu-item-${i}`}
                            value={type.id}
                          >
                            {type.codeName}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                </FormField>
              </AnalyticsSectionControls>
            </AnalyticsSectionHeader>

            <AnalyticsSectionGraphContainer>
              {/* TODO: Remove temp userId check below */}
              <PlaceholderImg
                onClick={() => navigate("/data-analytics")}
                src={user.userId === "tony.k.walters@cnmp.com" ? CaliforniaPlaceholder : ColoradoPlaceholder}
              />
            </AnalyticsSectionGraphContainer>
          </AnalyticsSection>
        </AnalyticsSectionCanvas>
      </RightSection>
    </Container>
  );
};

export default Dashboard;
